import type { UsefulPlaceCategory } from './usefulPlaces';

export type LoginResponse = {
  access_token: string;
  token_type: 'bearer';
  roles: string[];
};

export type RegisterPayload = {
  email: string;
  display_name: string;
  password: string;
};

export type RegisterResponse = {
  email: string;
};

export type SessionSummary = {
  id: number;
  label: string;
  started_at: string;
  locations_count: number;
  last_recorded_at: string | null;
};

export type SessionLocation = {
  id: number;
  latitude: number;
  longitude: number;
  accuracy_meters: number | null;
  recorded_at: string;
};

export type McpKeySummary = {
  id: number;
  label: string;
  created_at: string;
  last_used_at: string | null;
};

export type TripDocument = {
  slug: string;
  title: string;
  document_type: string;
  summary: string;
  sequence: number;
  source_url?: string | null;
  source_provider?: string | null;
  storage_key?: string | null;
  original_filename?: string | null;
  mime_type?: string | null;
  file_size_bytes?: number | null;
  file_url?: string | null;
};

export type TripOverview = {
  name: string;
  center: { latitude: number; longitude: number };
  start_at: string | null;
  end_at: string | null;
  documents: TripDocument[];
};

export type HealthResponse = {
  status: string;
};

export type ProjectSummary = {
  slug: string;
  name: string;
  access_level: string;
  mode: 'consumer' | 'professional';
};

export type SubscriptionSummary = {
  plan: 'free' | 'plus';
  configured_plan: string;
  status: string;
  provider: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  limits: Record<string, number>;
  storage: StorageUsage;
};
export type BillingInterval = 'monthly' | 'yearly';
export type StorageUsage = { used_bytes:number; quota_bytes:number; remaining_bytes:number; percentage:number; over_quota:boolean };

export type MeResponse = {
  user_id: number;
  email: string;
  display_name: string;
  roles: string[];
  subscription: SubscriptionSummary;
  projects: ProjectSummary[];
  default_project_slug: string | null;
};

export type ProjectDetail = {
  slug: string;
  name: string;
  description: string;
  mode: 'consumer' | 'professional';
  access_level: string;
  owner_email: string;
  center: { latitude: number; longitude: number };
  start_at: string | null;
  end_at: string | null;
  updated_at: string;
};

export type ProjectCreatePayload = {
  name: string;
  description?: string;
  mode?: ProjectDetail['mode'];
  center_latitude?: number;
  center_longitude?: number;
  start_at?: string | null;
  end_at?: string | null;
};

type ProjectMember = {
  user_id: number;
  email: string;
  display_name: string;
  access_level: string;
};

type ProjectInvite = {
  id: number;
  email: string;
  access_level: string;
  accepted_at: string | null;
  created_at: string;
};

export type ProjectShares = {
  members: ProjectMember[];
  invites: ProjectInvite[];
};

export type ProjectUpdatePayload = {
  name?: string;
  description?: string;
  mode?: ProjectDetail['mode'];
  center_latitude?: number;
  center_longitude?: number;
  start_at?: string | null;
  end_at?: string | null;
};

export type DocumentImportPayload = {
  title: string;
  source_url: string;
  document_type: string;
  summary: string;
};

export type PlannerStateResponse = {
  payload: unknown;
  updated_at: string | null;
};

export type CatalogLocation = {
  id: number;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  category: UsefulPlaceCategory;
  source: 'manual' | 'osm' | 'wikidata';
  source_id: string | null;
  source_metadata: Record<string, string>;
  visibility: 'private' | 'shared' | 'public';
  owner_user_id: number;
  owner_email: string;
  access_level: string;
  review_count: number;
  average_rating: number | null;
  project_slugs: string[];
  updated_at: string;
};

export type LocationReview = {
  id: number;
  rating: number;
  comment: string;
  author_email: string;
  created_at: string;
  updated_at: string;
};

export type SystemLocationPayload = {
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
  category?: UsefulPlaceCategory;
  source: 'osm' | 'wikidata' | 'manual';
  source_id: string;
  source_metadata?: Record<string, string>;
  visibility?: CatalogLocation['visibility'];
};

function normalizeLocationSourceMetadata(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
}

export type EvidenceItem = {
  id: number;
  project_slug: string;
  created_by_email: string;
  evidence_type: 'photo' | 'file' | 'note' | 'issue' | 'observation';
  title: string;
  note: string;
  session_id: number | null;
  location_id: number | null;
  field_location_id: number | null;
  latitude: number | null;
  longitude: number | null;
  accuracy_meters: number | null;
  position_recorded_at?: string | null;
  captured_at?: string;
  file_url: string | null;
  storage_key: string | null;
  file_name: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  created_at: string;
  updated_at: string;
};

export type EvidenceCreatePayload = {
  evidence_type: EvidenceItem['evidence_type'];
  title: string;
  note?: string;
  session_id?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  accuracy_meters?: number | null;
  position_recorded_at?: string | null;
  file_url?: string | null;
  storage_key?: string | null;
  file_name?: string | null;
  mime_type?: string | null;
  file_size_bytes?: number | null;
};

export type StorageUrlResponse = {
  url: string;
  method: string;
  headers: Record<string, string>;
};

export type EvidenceUploadUrlPayload = {
  file_name: string;
  mime_type: string;
  file_size_bytes: number;
};

export type EvidenceUploadUrlResponse = {
  storage_key: string;
  upload: StorageUrlResponse;
  download: StorageUrlResponse;
};

export type BillingPlan = {
  id: string;
  name: string;
  description: string;
  price_monthly_eur: number | null;
  price_yearly_eur: number | null;
  limits: Record<string, number>;
  capabilities: string[];
  is_current: boolean;
};

export type CheckoutSessionResponse = {
  status: 'redirect' | 'completed';
  url: string | null;
  subscription: SubscriptionSummary | null;
};

export type PortalSessionResponse = {
  url: string;
};

export type CancelSubscriptionResponse = {
  status: 'redirect' | 'completed';
  url: string | null;
  subscription: SubscriptionSummary | null;
};

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) { super(message); this.name = 'ApiError'; }
}
import { supabase } from '../lib/supabase';
import { signIn, signUp } from '../data/auth';
import { downloadEvidence, evidenceObjectPath, removeEvidenceObject, signedEvidenceUrls, uploadEvidence } from '../data/evidence';
import {
  beginOfflineTrip,
  cacheCurrentRouteResilience,
  cancelOfflineTripUpdate,
  cacheOfflineMediaFiles,
  cacheResilienceMediaFiles,
  completeOfflineTrip,
  hasOfflineMedia,
  offlineMediaUrl,
  getOfflineTripStatus,
  isOfflineFallbackError,
  readOfflineProjectIndex,
  readResilienceDocumentSlugs,
  readThroughOfflineSnapshot,
  readThroughResilienceSnapshot,
  readyOfflineTripSlugs,
  removeOfflineTrip,
  requestPersistentOfflineStorage,
  setOfflineCacheIdentity,
  setOfflineEntitlement,
  writeOfflineProjectIndex,
} from './offlineTripCache';
import { notifyOfflineProjectChanged } from './offlineSync';
export type { OfflineTripStatus } from './offlineTripCache';

function fail(error: { message: string; code?: string } | null): void { if (error) throw new ApiError(error.message, 400, error.code); }
async function projectBySlug(slug: string) { const { data, error } = await supabase.from('projects').select('*').eq('slug', slug).maybeSingle(); fail(error); if (!data) throw new ApiError('We couldn’t find that trip — it may have been deleted, or you may not have access to it.', 404, 'PROJECT_NOT_FOUND'); return data; }
async function currentUser() { const { data, error } = await supabase.from('users').select('*').maybeSingle(); fail(error); if (!data) throw new ApiError('Your session has expired. Please sign in again.', 401); return data; }

type TravelDocumentCacheScope = 'plus' | 'route';

function travelDocumentMediaKey(storageKey: string, scope: TravelDocumentCacheScope): string {
  return `${scope === 'plus' ? 'document' : 'route-document'}:${storageKey}`;
}

async function travelDocumentMediaEntries(documents: TripDocument[], scope: TravelDocumentCacheScope) {
  const entries: Array<{ storageKey: string; blob?: Blob }> = [];
  for (const document of documents) {
    if (!document.storage_key) continue;
    const cacheKey = travelDocumentMediaKey(document.storage_key, scope);
    if (await hasOfflineMedia(cacheKey)) {
      entries.push({ storageKey: cacheKey });
      continue;
    }
    const { data: blob, error } = await supabase.storage.from('documents').download(document.storage_key);
    if (error || !blob) throw new Error(error?.message ?? `Could not download travel document ${document.title}`);
    entries.push({ storageKey: cacheKey, blob });
  }
  return entries;
}

async function cacheCurrentRouteTravelDocuments(slug: string, documents: TripDocument[]): Promise<void> {
  const documentSlugs = await readResilienceDocumentSlugs(slug);
  if (documentSlugs.length === 0) return;
  const allowed = new Set(documentSlugs);
  const entries = await travelDocumentMediaEntries(documents.filter((document) => allowed.has(document.slug)), 'route');
  await cacheResilienceMediaFiles(entries);
}

async function hydrateTravelDocumentFiles(documents: TripDocument[]): Promise<TripDocument[]> {
  return Promise.all(documents.map(async (document) => {
    if (!document.storage_key) return document;
    const plusKey = travelDocumentMediaKey(document.storage_key, 'plus');
    if (await hasOfflineMedia(plusKey)) return { ...document, file_url: offlineMediaUrl(plusKey) };
    const routeKey = travelDocumentMediaKey(document.storage_key, 'route');
    if (await hasOfflineMedia(routeKey)) return { ...document, file_url: offlineMediaUrl(routeKey) };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ...document, file_url: null };
    const { data, error } = await supabase.storage.from('documents').createSignedUrl(document.storage_key, 3600);
    return error ? { ...document, file_url: null } : { ...document, file_url: data.signedUrl };
  }));
}

/** Compatibility facade that keeps existing view-models stable while all I/O uses Supabase. */
export function createApiClient(_unusedBaseUrl = '') {
  const facade = {
    login: async (email: string, password: string) => { const result = await signIn(email,password); return { access_token: result.session!.access_token, token_type:'bearer' as const, roles:[] }; },
    register: async (payload: RegisterPayload) => { await signUp(payload.email,payload.password,payload.display_name); return { email: payload.email }; },
    health: async () => ({ status:'ok' }),
    me: async (_token: string): Promise<MeResponse> => { await supabase.rpc('accept_project_invites'); const user=await currentUser(); const projects=await api.listProjects(''); const subscription=await api.getSubscription(''); return { user_id:user.id,email:user.email,display_name:user.display_name,roles:[],subscription,projects,default_project_slug:projects[0]?.slug??null }; },
    getSubscription: async (_token: string): Promise<SubscriptionSummary> => {
      const [{data,error},{data:usage,error:usageError}]=await Promise.all([
        supabase.from('user_subscriptions').select('*').maybeSingle(),
        supabase.rpc('get_storage_summary' as never),
      ]);
      fail(error);
      fail(usageError);
      const subscription: SubscriptionSummary = {
        plan:(data?.plan??'free') as SubscriptionSummary['plan'],
        configured_plan:data?.plan??'free',
        status:data?.status??'active',
        provider:data?.provider??null,
        current_period_end:data?.current_period_end??null,
        cancel_at_period_end:data?.cancel_at_period_end??false,
        limits:{evidence:data?.plan==='plus'?5000:500,documents:data?.plan==='plus'?100:5},
        storage:usage as unknown as StorageUsage,
      };
      const { data: sessionData } = await supabase.auth.getSession();
      await setOfflineCacheIdentity(sessionData.session?.user.id ?? null);
      const plusEntitled = subscription.plan === 'plus' && ['active','trialing','past_due'].includes(subscription.status);
      await setOfflineEntitlement(plusEntitled);
      return subscription;
    },
    getBillingPlans: async (_token?: string) => [
      {
        id:'free',
        name:'Free',
        description:'Unlimited trips, planning, sharing, and 500 MB storage',
        price_monthly_eur:0,
        price_yearly_eur:0,
        limits:{evidence:500,documents:5},
        capabilities:['planning','sharing'],
        is_current:true,
      },
      {
        id:'plus',
        name:'Plus',
        description:'Offline trips, 5 GB storage, larger uploads, and more room for memories and travel info',
        price_monthly_eur:3.99,
        price_yearly_eur:39.99,
        limits:{evidence:5000,documents:100},
        capabilities:['planning','sharing','imports','offline_trips'],
        is_current:false,
      },
    ],
    createCheckoutSession: async (_t:string, plan:string, interval:BillingInterval, success_url:string, cancel_url:string) => { const {data,error}=await supabase.functions.invoke('billing-checkout',{body:{plan,interval,success_url,cancel_url}}); fail(error); return data as CheckoutSessionResponse; },
    createPortalSession: async (_t:string, return_url:string) => { const {data,error}=await supabase.functions.invoke('billing-portal',{body:{return_url}}); fail(error); return data as PortalSessionResponse; },
    cancelSubscription: async (_t:string, return_url:string) => { const {data,error}=await supabase.functions.invoke('billing-cancel',{body:{return_url}}); fail(error); return data as CancelSubscriptionResponse; },
    deleteAccount: async (_token?: string) => { const {error}=await supabase.functions.invoke('account-delete'); fail(error); },
    exportMyData: async (_token?: string) => { const {data,error}=await supabase.functions.invoke('account-export'); fail(error); return data; },
    listProjects: async (_token?: string): Promise<ProjectSummary[]> => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const cached=await readOfflineProjectIndex<ProjectSummary[]>();
        if (cached) {
          const ready=new Set(await readyOfflineTripSlugs());
          return cached.filter((project)=>ready.has(project.slug));
        }
      }
      try {
        const me=await currentUser();
        const {data,error}=await supabase.from('projects').select('*').order('updated_at',{ascending:false});
        fail(error);
        const {data:mships,error:membershipError}=await supabase.from('project_memberships').select('project_id,access_level').eq('user_id',me.id);
        fail(membershipError);
        const levels=new Map((mships??[]).map(m=>[m.project_id as number,m.access_level as string]));
        const projects: ProjectSummary[]=(data??[]).map(p=>({
          slug:p.slug,
          name:p.name,
          mode:p.mode as ProjectSummary['mode'],
          access_level:p.owner_user_id===me.id?'owner':(levels.get(p.id)??'viewer'),
        }));
        await writeOfflineProjectIndex(projects);
        return projects;
      } catch (error) {
        if (!isOfflineFallbackError(error)) throw error;
        const cached=await readOfflineProjectIndex<ProjectSummary[]>();
        if (!cached) throw error;
        const ready=new Set(await readyOfflineTripSlugs());
        return cached.filter((project)=>ready.has(project.slug));
      }
    },
    createProject: async (_t:string,p:ProjectCreatePayload):Promise<ProjectDetail> => { const {data,error}=await supabase.rpc('create_project',{project_name:p.name,project_description:p.description,project_mode:p.mode,latitude:p.center_latitude,longitude:p.center_longitude}); fail(error); if(p.start_at!==undefined||p.end_at!==undefined){const {error:timingError}=await supabase.from('projects').update({start_at:p.start_at??null,end_at:p.end_at??null}).eq('id',data!.id); fail(timingError);} return api.getProject('',data!.slug); },
    getProject: async (_t:string,slug:string):Promise<ProjectDetail> => readThroughResilienceSnapshot(slug,'project',()=>readThroughOfflineSnapshot(slug,'project',async()=> {
      const p=await projectBySlug(slug);
      const me=await currentUser();
      let access_level='viewer';
      if(p.owner_user_id===me.id){access_level='owner';}
      else{
        const {data:m,error}=await supabase.from('project_memberships').select('access_level').eq('project_id',p.id).eq('user_id',me.id).maybeSingle();
        fail(error);
        access_level=m?.access_level??'viewer';
      }
      return {slug:p.slug,name:p.name,description:p.description,mode:p.mode,access_level,owner_email:me.email,center:{latitude:p.center_latitude,longitude:p.center_longitude},start_at:p.start_at,end_at:p.end_at,updated_at:p.updated_at};
    }),true),
    updateProject: async (_t:string,slug:string,p:ProjectUpdatePayload) => { const current=await projectBySlug(slug); const {error}=await supabase.from('projects').update({name:p.name,description:p.description,mode:p.mode,center_latitude:p.center_latitude,center_longitude:p.center_longitude,start_at:p.start_at,end_at:p.end_at,updated_at:new Date().toISOString()}).eq('id',current.id); fail(error); const updated=await api.getProject('',slug); notifyOfflineProjectChanged(slug); return updated; },
    deleteProject: async (_t:string,slug:string) => { const p=await projectBySlug(slug); const {error}=await supabase.from('projects').delete().eq('id',p.id); fail(error); return {status:'deleted'}; },
    listProjectShares: async (_t:string,slug:string):Promise<ProjectShares> => { const {data,error}=await supabase.rpc('get_project_shares' as never,{project_slug:slug} as never); fail(error); return data as unknown as ProjectShares; },
    createProjectShare: async (_t:string,slug:string,email:string,access_level:'viewer'|'editor') => { const {error}=await supabase.rpc('invite_project_member',{project_slug:slug,invite_email:email,invite_access:access_level}); fail(error); return api.listProjectShares('',slug); },
    deleteProjectShare: async (_t:string,slug:string,userId:number) => { const p=await projectBySlug(slug); const {error}=await supabase.from('project_memberships').delete().eq('project_id',p.id).eq('user_id',userId); fail(error); return api.listProjectShares('',slug); },
    cancelProjectInvite: async (_t:string,slug:string,inviteId:number) => { const {error}=await supabase.rpc('cancel_project_invite' as never,{project_slug:slug,invite_id:inviteId} as never); fail(error); return api.listProjectShares('',slug); },
    stopProjectSharing: async (_t:string,slug:string) => { const {error}=await supabase.rpc('stop_project_sharing' as never,{project_slug:slug} as never); fail(error); return api.listProjectShares('',slug); },
    getProjectOverview: async (_t:string,slug:string):Promise<TripOverview> => {
      const overview = await readThroughResilienceSnapshot(slug,'overview',()=>readThroughOfflineSnapshot(slug,'overview',async()=> {
        const p=await projectBySlug(slug);
        const {data,error}=await supabase.from('trip_documents').select('*').eq('project_id',p.id).order('sequence');
        fail(error);
        return {name:p.name,center:{latitude:p.center_latitude,longitude:p.center_longitude},start_at:p.start_at,end_at:p.end_at,documents:(data??[]) as TripDocument[]};
      }),true);
      if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        await cacheCurrentRouteTravelDocuments(slug, overview.documents).catch(()=>undefined);
      }
      return { ...overview, documents: await hydrateTravelDocumentFiles(overview.documents) };
    },
    getProjectPlannerState: async (_t:string,slug:string) => {
      const state = await readThroughResilienceSnapshot(slug,'planner-state',()=>readThroughOfflineSnapshot(slug,'planner-state',async()=> {
        const p=await projectBySlug(slug);
        const {data,error}=await supabase.from('project_planner_states').select('*').eq('project_id',p.id).maybeSingle();
        fail(error);
        return {payload:data?.payload??{},updated_at:data?.updated_at??null};
      }),true);
      if (typeof navigator === 'undefined' || navigator.onLine !== false) void api.getProjectOverview('',slug).catch(()=>undefined);
      return state;
    },
    saveProjectPlannerState: async (_t:string,slug:string,payload:unknown,expected_updated_at?:string|null) => { const {data,error}=await supabase.rpc('update_planner_state',{project_slug:slug,new_payload:payload as never,expected_updated_at}); fail(error); const saved={payload:data!.payload,updated_at:data!.updated_at}; const routeContextChanged=await cacheCurrentRouteResilience(slug,saved); if(routeContextChanged) await api.getProjectOverview('',slug).catch(()=>undefined); notifyOfflineProjectChanged(slug); return saved; },
    listProjectSessions: async (_t:string,slug:string):Promise<SessionSummary[]> => readThroughResilienceSnapshot(slug,'sessions',()=>readThroughOfflineSnapshot(slug,'sessions',async()=> {
      const p=await projectBySlug(slug);
      const {data,error}=await supabase.from('field_sessions').select('*').eq('project_id',p.id).order('started_at',{ascending:false});
      fail(error);
      return (data??[]).map(s=>({...s,locations_count:0,last_recorded_at:null}));
    }),true),
    startProjectSession: async (_t:string,slug:string,label:string):Promise<SessionSummary> => { const {data,error}=await supabase.rpc('start_field_session',{project_slug:slug,session_label:label}); fail(error); notifyOfflineProjectChanged(slug); return {...data!,locations_count:0,last_recorded_at:null}; },
    getProjectLocations: async (_t:string,slug:string,id:number) => readThroughResilienceSnapshot(slug,`session-locations:${id}`,()=>readThroughOfflineSnapshot(slug,`session-locations:${id}`,async()=> {
      const {data,error}=await supabase.from('field_locations').select('*').eq('session_id',id).order('recorded_at');
      fail(error);
      return data??[];
    })),
    pushProjectLocation: async (_t:string,_s:string,id:number,latitude:number,longitude:number,accuracy_meters:number|null) => { const {error}=await supabase.from('field_locations').insert({session_id:id,latitude,longitude,accuracy_meters}); fail(error); return {status:'created'}; },
    deleteProjectSession: async (_t:string,slug:string,id:number) => { const {error}=await supabase.from('field_sessions').delete().eq('id',id); fail(error); notifyOfflineProjectChanged(slug); return {status:'deleted'}; },
    renameProjectSession: async (_t:string,slug:string,id:number,label:string) => { const {data,error}=await supabase.from('field_sessions').update({label}).eq('id',id).select().single(); fail(error); notifyOfflineProjectChanged(slug); return {...data!,locations_count:0,last_recorded_at:null}; },
    listProjectEvidence: async (_t:string,slug:string,sessionId?:number|null):Promise<EvidenceItem[]> => {
      const items = await readThroughOfflineSnapshot(slug,`evidence:${sessionId??'all'}`,async()=> {
      const p=await projectBySlug(slug);
      let q=supabase.from('evidence_items').select('*').eq('project_id',p.id);
      if(sessionId!=null)q=q.eq('session_id',sessionId);
      const {data,error}=await q.order('created_at',{ascending:false});
      fail(error);
      const items=(data??[]).map(e=>({...e,project_slug:slug,created_by_email:''}) as EvidenceItem);
      const urls=await signedEvidenceUrls(items.map(e=>e.storage_key).filter((k):k is string=>Boolean(k)));
        return items.map(e=>e.storage_key?{...e,file_url:urls.get(e.storage_key)??null}:e);
      });
      return Promise.all(items.map(async (item) => {
        if (!item.storage_key || !(await hasOfflineMedia(item.storage_key))) return item;
        return { ...item, file_url: offlineMediaUrl(item.storage_key) };
      }));
    },
    createProjectEvidence: async (_t:string,slug:string,payload:EvidenceCreatePayload):Promise<EvidenceItem> => { const u=await currentUser(); const memory={...payload,memory_type:payload.evidence_type,track_id:payload.session_id,file_url:undefined}; const {data,error}=await supabase.rpc('create_memory' as never,{project_slug:slug,memory:memory as never} as never); fail(error); const created={...(data as unknown as Record<string, unknown>),project_slug:slug,created_by_email:u.email} as EvidenceItem; if(created.storage_key)created.file_url=await downloadEvidence(created.storage_key); notifyOfflineProjectChanged(slug); return created; },
    deleteProjectEvidence: async (_t:string,slug:string,id:number) => { const {data}=await supabase.from('evidence_items').select('storage_key').eq('id',id).single(); if(data?.storage_key) await supabase.storage.from('evidence').remove([data.storage_key]); const {error}=await supabase.from('evidence_items').delete().eq('id',id); fail(error); notifyOfflineProjectChanged(slug); return {status:'deleted'}; },
    getEvidenceUploadUrl: async (_t:string,slug:string,payload:EvidenceUploadUrlPayload) => { const p=await projectBySlug(slug); const {data}=await supabase.auth.getUser(); const key=evidenceObjectPath(p.id,data.user!.id,payload.file_name); const {data:reservation,error}=await supabase.rpc('reserve_storage_upload' as never,{target_bucket:'evidence',target_path:key,byte_count:payload.file_size_bytes} as never); fail(error); return {storage_key:key,reservation_id:(reservation as unknown as {id:string}).id,upload:{url:key,method:'SUPABASE_STORAGE',headers:{}},download:{url:key,method:'SIGNED_URL',headers:{}}}; },
    uploadEvidenceFile: async (key:string,file:File) => { await uploadEvidence(key,file); return downloadEvidence(key); },
    cleanupEvidenceUpload: async (key:string) => { await removeEvidenceObject(key); },
    listLocations: async (_token?: string):Promise<CatalogLocation[]> => { const me=await currentUser(); const {data,error}=await supabase.from('locations').select('*'); fail(error); return (data??[]).map(l=>({...l,source_metadata:normalizeLocationSourceMetadata(l.source_metadata),owner_email:'',access_level:l.owner_user_id===me.id?'owner':'viewer',review_count:0,average_rating:null,project_slugs:[]})); },
    createLocation: async (_t:string,payload:SystemLocationPayload):Promise<CatalogLocation> => { const u=await currentUser(); const {data,error}=await supabase.from('locations').insert({name:payload.name,description:payload.description??'',latitude:payload.latitude,longitude:payload.longitude,category:payload.category??'other',source:payload.source,source_id:payload.source_id,source_metadata:payload.source_metadata??{},visibility:payload.visibility??'private',owner_user_id:u.id}).select().single(); fail(error); return {...data!,source_metadata:normalizeLocationSourceMetadata(data!.source_metadata),owner_email:u.email,access_level:'owner',review_count:0,average_rating:null,project_slugs:[]}; },
    listProjectLocations: async (_t:string,slug:string):Promise<CatalogLocation[]> => readThroughOfflineSnapshot(slug,'project-locations',async()=> {
      const p=await projectBySlug(slug);
      const {data,error}=await supabase.from('location_projects').select('location_id').eq('project_id',p.id);
      fail(error);
      const all=await api.listLocations('');
      return all.filter(l=>(data??[]).some(x=>x.location_id===l.id));
    }),
    addProjectLocation: async (_t:string,slug:string,id:number) => { const p=await projectBySlug(slug),u=await currentUser(); const {error}=await supabase.from('location_projects').insert({project_id:p.id,location_id:id,added_by_user_id:u.id}); fail(error); const locations=await api.listProjectLocations('',slug); notifyOfflineProjectChanged(slug); return locations; },
    // Locations default to private: the RLS read policy still shares them with members of
    // the trips they are linked to, but they stay out of other users' Location Library.
    addSystemProjectLocation: async (_t:string,slug:string,payload:SystemLocationPayload) => { const p=await projectBySlug(slug),u=await currentUser(); const {data,error}=await supabase.from('locations').insert({name:payload.name,description:payload.description??'',latitude:payload.latitude,longitude:payload.longitude,category:payload.category??'other',source:payload.source,source_id:payload.source_id,source_metadata:payload.source_metadata??{},visibility:payload.visibility??'private',owner_user_id:u.id}).select().single(); fail(error); const {error:linkError}=await supabase.from('location_projects').insert({project_id:p.id,location_id:data!.id,added_by_user_id:u.id}); fail(linkError); notifyOfflineProjectChanged(slug); return {...data!,source_metadata:normalizeLocationSourceMetadata(data!.source_metadata),owner_email:u.email,access_level:'owner',review_count:0,average_rating:null,project_slugs:[slug]}; },
    removeProjectLocation: async (_t:string,slug:string,id:number) => { const p=await projectBySlug(slug); const {error}=await supabase.from('location_projects').delete().eq('project_id',p.id).eq('location_id',id); fail(error); notifyOfflineProjectChanged(slug); },
    createMcpKey: async (_t:string,label:string) => { const {data,error}=await supabase.rpc('create_mcp_key',{key_label:label}); fail(error); return data as string; },
    listMcpKeys: async (_t:string):Promise<McpKeySummary[]> => { const {data,error}=await supabase.from('mcp_keys').select('id,label,created_at,last_used_at').order('created_at',{ascending:false}); fail(error); return (data??[]) as McpKeySummary[]; },
    revokeMcpKey: async (_t:string,id:number) => { const {error}=await supabase.from('mcp_keys').delete().eq('id',id); fail(error); return {status:'deleted'}; },
    deleteLocation: async (_t:string,id:number) => { const {error}=await supabase.from('locations').delete().eq('id',id); fail(error); return {status:'deleted'}; },
    updateLocation: async (_t:string,id:number,payload:Record<string,unknown>) => { const {data,error}=await supabase.from('locations').update(payload as never).eq('id',id).select().single(); fail(error); return {...data!,source_metadata:normalizeLocationSourceMetadata(data!.source_metadata),owner_email:'',access_level:'owner',review_count:0,average_rating:null,project_slugs:[]}; },
    reviewLocation: async (_t:string,id:number,rating:number,comment='') => { const u=await currentUser(); const {data,error}=await supabase.from('location_reviews').upsert({location_id:id,user_id:u.id,rating,comment}).select().single(); fail(error); return {...data!,author_email:u.email}; },
    importProjectDocument: async (_t:string,slug:string,p:DocumentImportPayload) => { const {data,error}=await supabase.rpc('create_document',{project_slug:slug,doc:{title:p.title,document_type:p.document_type,summary:p.summary,source_url:p.source_url} as never}); fail(error); notifyOfflineProjectChanged(slug); return data!; },
    deleteProjectDocument: async (_t:string,slug:string,docSlug:string) => { const p=await projectBySlug(slug); const {error}=await supabase.from('trip_documents').delete().eq('project_id',p.id).eq('slug',docSlug); fail(error); notifyOfflineProjectChanged(slug); return {status:'deleted'}; },
    getOfflineProjectStatus: async (slug:string) => getOfflineTripStatus(slug),
    downloadProjectOffline: async (token:string,slug:string) => {
      const subscription=await api.getSubscription(token);
      const plusEntitled=subscription.plan==='plus' && ['active','trialing','past_due'].includes(subscription.status);
      if(!plusEntitled) throw new ApiError('An active Doddledays Plus subscription is required for offline trips.',402,'PLUS_REQUIRED');
      await beginOfflineTrip(slug);
      try {
        await requestPersistentOfflineStorage();
        const [project,overview,planner,sessions,evidence,locations]=await Promise.all([
          api.getProject(token,slug),
          api.getProjectOverview(token,slug),
          api.getProjectPlannerState(token,slug),
          api.listProjectSessions(token,slug),
          api.listProjectEvidence(token,slug),
          api.listProjectLocations(token,slug),
        ]);
        void planner;
        void locations;
        await Promise.all(sessions.flatMap((session)=>[
          api.getProjectLocations(token,slug,session.id),
          api.listProjectEvidence(token,slug,session.id),
        ]));
        const mediaEntries: Array<{ storageKey: string; blob?: Blob }> = [];
        for (const item of evidence) {
          if (!item.storage_key) continue;
          if (await hasOfflineMedia(item.storage_key)) {
            mediaEntries.push({ storageKey: item.storage_key });
            continue;
          }
          const { data: blob, error: mediaError } = await supabase.storage.from('evidence').download(item.storage_key);
          if (mediaError || !blob) throw new Error(mediaError?.message ?? `Could not download memory photo ${item.storage_key}`);
          mediaEntries.push({ storageKey: item.storage_key, blob });
        }
        const documentEntries = await travelDocumentMediaEntries(overview.documents, 'plus');
        await cacheOfflineMediaFiles(slug, [...mediaEntries, ...documentEntries]);
        await completeOfflineTrip(slug);
        const existingIndex = await readOfflineProjectIndex<ProjectSummary[]>() ?? [];
        await writeOfflineProjectIndex([
          ...existingIndex.filter((item)=>item.slug!==slug),
          { slug:project.slug,name:project.name,mode:project.mode,access_level:project.access_level },
        ]);
        await api.listProjects(token).catch(()=>undefined);
        return api.getOfflineProjectStatus(slug);
      } catch(error) {
        await cancelOfflineTripUpdate(slug);
        throw error;
      }
    },
    removeProjectOffline: async (_token:string,slug:string) => {
      await removeOfflineTrip(slug);
      return api.getOfflineProjectStatus(slug);
    },
    listRoles: async () => ['admin','editor','viewer'],
  };
  const api = facade;
  return facade;
}
